import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Flame, Clock, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { gamesApi, Game } from '../services/api';

interface OyunData {
  id: string;
  name: string;
  imageUrl: string;
  provider: string;
  category: string;
}

const PopulerOyunlar: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [oyunlar, setOyunlar] = useState<OyunData[]>([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchOyunlar = async () => {
      try {
        const result = await gamesApi.list({ limit: 8 });
        if (result.success && result.data?.games) {
          const mappedGames: OyunData[] = result.data.games.map((game: Game) => ({
            id: game.id,
            name: game.name,
            imageUrl: game.thumbnailUrl || '',
            provider: game.provider,
            category: game.category,
          }));
          setOyunlar(mappedGames);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Oyunlar yüklenemedi:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchOyunlar();
  }, []);

  // Henüz oyun verisi yoksa "Çok Yakında" göster
  if (!loading && (error || oyunlar.length === 0)) {
    return (
      <div className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-6 border border-dark-700 h-full flex flex-col">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
              <Flame className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">En Popüler Oyunlar</h2>
          </div>
          <Link to="/slotlar/populer" className="text-primary-400 hover:text-primary-300 text-sm">
            Tümünü Gör →
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Çok Yakında!</h3>
            <p className="text-gray-400 text-sm">
              En popüler oyunlar burada yer alacak.
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-6 border border-dark-700 h-full flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 flex items-center justify-center">
            <Flame className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-xl font-bold text-white">En Popüler Oyunlar</h2>
        </div>
        <Link to="/slotlar/populer" className="text-primary-400 hover:text-primary-300 text-sm">
          Tümünü Gör →
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1">
        {loading ? (
          <>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="aspect-square bg-dark-700/50 rounded-xl animate-pulse" />
            ))}
          </>
        ) : (
          oyunlar.map((oyun, index) => (
            <motion.div
              key={oyun.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer"
            >
              {oyun.imageUrl ? (
                <img
                  src={oyun.imageUrl}
                  alt={oyun.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
                  <Play className="w-8 h-8 text-white" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-dark-900/90 via-dark-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <p className="text-white font-medium text-sm truncate">{oyun.name}</p>
                  <p className="text-gray-400 text-xs">{oyun.provider}</p>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-primary-500/90 flex items-center justify-center transform scale-0 group-hover:scale-100 transition-transform duration-300">
                    <Play className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default PopulerOyunlar;
